package com.eladfinish.bible.contest.screens.search.engine

/**
 * Kotlin/JS's `MatchGroup` has no `range`, so the match is repeated on a native `RegExp` with
 * the `d` flag, which makes the browser report each group's indices. The `y` flag pins that
 * second run to where [match] starts, so it is the same match and not a search.
 */
internal actual fun groupRangesOf(regex: Regex, input: CharSequence, match: MatchResult): List<IntRange?>? {
    val native = nativeRegExpWithIndices(regex) ?: return null
    native.lastIndex = match.range.first
    val result = native.exec(input.toString()) ?: return null
    if (result.index != match.range.first || result[0] != match.value) return null
    val indices = result.indices ?: return null

    val groupCount = (result.length as Int) - 1
    return (1..groupCount).map { group ->
        val bounds = indices[group] ?: return@map null
        (bounds[0] as Int) until (bounds[1] as Int)
    }
}

// A search runs one Regex over every verse of the Bible, so one compiled RegExp is enough.
private var compiledFor: Regex? = null
private var compiled: dynamic = null

/** `null` where the engine predates the `d` flag (Safari before 15): the constructor throws. */
private fun nativeRegExpWithIndices(regex: Regex): dynamic {
    if (compiledFor !== regex) {
        val pattern = regex.pattern
        // "u" as Kotlin's own Regex compiles it, so both runs read the pattern the same way.
        val flags = buildString {
            append("dyu")
            if (RegexOption.IGNORE_CASE in regex.options) append('i')
            if (RegexOption.MULTILINE in regex.options) append('m')
        }
        compiled = try {
            js("new RegExp(pattern, flags)")
        } catch (e: Throwable) {
            null
        }
        compiledFor = regex
    }
    return compiled
}
